import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { Upload, FileText, Loader2 } from "lucide-react";
import { errMsg } from "../../lib/apiErrors";

// Câmpuri pe care le mapăm din OCR -> model Car.
// label = ce vede utilizatorul, key = câmp în Car model
const TALON_FIELD_MAP = [
  { key: "numar_inmatriculare", label: "Număr înmatriculare (Kontrollschild)" },
  { key: "marca", label: "Marcă" },
  { key: "model", label: "Model" },
  { key: "chassis_nr", label: "Chassis Nr (Fahrgestellnummer / VIN)" },
  { key: "stamm_nr", label: "Stamm Nr (Stammnummer)" },
  { key: "typenschein", label: "Typenschein" },
  { key: "an_fabricatie", label: "An fabricație (câmp 36 — Baujahr)" },
  { key: "culoare", label: "Culoare" },
  { key: "mfk_last", label: "Ultimul MFK (câmp 39 — Letzte Prüfung)" },
  { key: "mfk_expire", label: "MFK expiră (auto: ultimul MFK + 2 ani)" },
  { key: "data_achizitie", label: "Data cumpărării (câmp 38 — Im Verkehr gesetzt am)" },
  { key: "prima_inmatriculare", label: "Prima înmatriculare (câmp 1 — 1. Inverkehrsetzung)" },
];

export default function TalonRescanDialog({ open, car, onClose, onApplied }) {
  const [busy, setBusy] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [selected, setSelected] = useState({});
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setExtracted(null);
      setSelected({});
      setFile(null);
    }
  }, [open]);

  // Generează / curăță URL-ul de preview pentru fișierul încărcat
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const isPdf = file?.type === "application/pdf" || /\.pdf$/i.test(file?.name || "");
  const isImage = (file?.type || "").startsWith("image/") || /\.(jpe?g|png|webp|heic|heif)$/i.test(file?.name || "");

  const pickFile = () => inputRef.current?.click();

  const onFile = async (f) => {
    if (!f) return;
    setFile(f);
    setBusy(true);
    setExtracted(null);
    const tid = toast.loading("📄 Citesc talonul cu AI...");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/cars/extract-talon", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const x = res.data?.extracted || {};
      // ⚠️ NU mai aliasăm prima_inmatriculare → data_achizitie.
      // Sunt 3 câmpuri distincte pe talon: câmp 1 (prima_inmatriculare), câmp 36 (an_fabricatie),
      // câmp 38 (data_achizitie). Backend prompt asigură mapping-ul corect.
      setExtracted(x);
      // pre-bifează doar câmpurile cu valoare nouă (diferă de cea existentă) sau cele goale în mașină
      const preset = {};
      for (const { key } of TALON_FIELD_MAP) {
        const newVal = x[key];
        const oldVal = car?.[key];
        if (newVal != null && newVal !== "" && String(newVal) !== String(oldVal || "")) {
          preset[key] = true;
        }
      }
      setSelected(preset);
      const nFields = Object.values(preset).filter(Boolean).length;
      toast.success(`Talon citit: ${nFields} câmp${nFields === 1 ? "" : "uri"} cu valori noi/diferite`, { id: tid });
    } catch (e) {
      toast.error(errMsg(e, "Eroare la citirea talonului"), { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };

  const apply = async () => {
    if (!extracted || !car) return;
    const patch = {};
    for (const { key } of TALON_FIELD_MAP) {
      if (selected[key]) {
        let v = extracted[key];
        if (key === "an_fabricatie" && v != null) {
          const n = parseInt(v, 10);
          v = Number.isNaN(n) ? null : n;
        }
        patch[key] = v;
      }
    }
    if (Object.keys(patch).length === 0) {
      toast.info("Nu ai bifat niciun câmp pentru update");
      return;
    }
    setBusy(true);
    try {
      // Trimitem PATCH-style — server PUT-ul așteaptă obiect Car întreg, deci facem merge
      await api.put(`/cars/${car.id}`, { ...car, ...patch });
      toast.success(`${Object.keys(patch).length} câmp${Object.keys(patch).length === 1 ? "" : "uri"} actualizat${Object.keys(patch).length === 1 ? "" : "e"} din talon`);
      onApplied && onApplied();
      onClose && onClose();
    } catch (e) {
      toast.error(errMsg(e, "Eroare la salvare"));
    } finally {
      setBusy(false);
    }
  };

  const fieldRow = ({ key, label }) => {
    const newVal = extracted?.[key];
    const oldVal = car?.[key];
    const hasNew = newVal != null && newVal !== "";
    const isSame = hasNew && String(newVal) === String(oldVal || "");
    const isDiff = hasNew && !isSame;
    return (
      <label
        key={key}
        className={`flex items-start gap-2 p-2 rounded border text-sm cursor-pointer transition-colors ${
          !hasNew ? "bg-zinc-50 border-zinc-200 opacity-50 cursor-not-allowed"
          : isSame ? "bg-emerald-50 border-emerald-200"
          : "bg-amber-50 border-amber-300 hover:bg-amber-100"
        }`}
        data-testid={`talon-rescan-row-${key}`}
      >
        <Checkbox
          className="mt-0.5"
          disabled={!hasNew}
          checked={!!selected[key]}
          onCheckedChange={(v) => setSelected((s) => ({ ...s, [key]: !!v }))}
          data-testid={`talon-rescan-check-${key}`}
        />
        <span className="flex-1 min-w-0">
          <div className="font-semibold text-zinc-800 text-[12px]">{label}</div>
          <div className="mt-0.5 flex flex-col gap-0.5 text-[11px]">
            <div className="text-zinc-500">
              Actual: <span className="text-zinc-800 font-mono">{oldVal == null || oldVal === "" ? "—" : String(oldVal)}</span>
            </div>
            <div className={isDiff ? "text-amber-800 font-semibold" : "text-zinc-500"}>
              {hasNew ? <>Talon: <span className="font-mono">{String(newVal)}</span></> : <em>Lipsește în talon</em>}
              {isSame && <span className="ml-1 text-[10px] text-emerald-700 font-bold uppercase">= identic</span>}
              {isDiff && oldVal && <span className="ml-1 text-[10px] text-amber-700 font-bold uppercase">≠ diferit</span>}
              {isDiff && !oldVal && <span className="ml-1 text-[10px] text-blue-700 font-bold uppercase">+ nou</span>}
            </div>
          </div>
        </span>
      </label>
    );
  };

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v && !busy) onClose && onClose(); }}>
      <DialogContent
        className={`${extracted ? "max-w-7xl" : "max-w-3xl"} max-h-[95vh] flex flex-col`}
        data-testid="talon-rescan-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={18} className="text-cyan-600"/>
            Re-scanează talon — {car?.numar_inmatriculare || "—"}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Încarcă o poză sau PDF cu talonul (Fahrzeugausweis). AI extrage marca/model/Fahrgestellnummer/Typenschein/Kontrollschield/MFK/Data cumpărării și poți alege ce câmpuri suprascrii.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {!extracted ? (
            // Faza 1: încărcare fișier — drop-zone single column
            <div className="overflow-y-auto space-y-3 pr-1 h-full">
              <div
                onClick={pickFile}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={onDrop}
                className="rounded-lg border-2 border-dashed border-cyan-400 bg-cyan-50/50 p-6 text-center cursor-pointer hover:bg-cyan-50 transition-colors"
                data-testid="talon-rescan-dropzone"
              >
                {busy ? (
                  <div className="flex flex-col items-center gap-2 text-cyan-700">
                    <Loader2 className="animate-spin" size={28}/>
                    <span className="text-sm font-semibold">Analizez talonul cu AI...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1.5 text-cyan-800">
                    <Upload size={28}/>
                    <span className="text-sm font-semibold">Apasă sau trage un fișier aici</span>
                    <span className="text-[11px] text-zinc-600">Acceptat: PDF, JPG, PNG, WEBP, HEIC · max 20MB</span>
                    {file && <span className="text-[11px] text-cyan-700 mt-1">📎 {file.name}</span>}
                  </div>
                )}
                <input
                  ref={inputRef}
                  type="file"
                  hidden
                  accept="image/*,application/pdf"
                  onChange={(e) => onFile(e.target.files?.[0])}
                  data-testid="talon-rescan-file-input"
                />
              </div>
            </div>
          ) : (
            // Faza 2: preview talon + verificare câmpuri side-by-side
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 h-full min-h-0">
              {/* STÂNGA: preview talon */}
              <div className="border border-zinc-200 rounded-md bg-zinc-50 overflow-hidden flex flex-col min-h-0" data-testid="talon-rescan-preview-pane">
                <div className="flex-shrink-0 px-3 py-2 border-b border-zinc-200 bg-white text-[11px] flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <FileText size={12} className="text-cyan-600 flex-shrink-0"/>
                    <span className="font-semibold text-zinc-700 truncate" title={file?.name}>{file?.name || "Talon"}</span>
                  </div>
                  {previewUrl && (
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-700 hover:text-cyan-900 hover:underline whitespace-nowrap"
                      data-testid="talon-rescan-preview-open"
                    >
                      Deschide ↗
                    </a>
                  )}
                </div>
                <div className="flex-1 overflow-auto p-2 flex items-start justify-center">
                  {!previewUrl ? (
                    <div className="text-xs text-zinc-400 italic mt-8">Fără preview disponibil</div>
                  ) : isPdf ? (
                    <iframe
                      src={previewUrl}
                      title="Preview talon PDF"
                      className="w-full h-full min-h-[500px] border-0"
                      data-testid="talon-rescan-preview-pdf"
                    />
                  ) : isImage ? (
                    <img
                      src={previewUrl}
                      alt="Preview talon"
                      className="max-w-full h-auto object-contain"
                      data-testid="talon-rescan-preview-image"
                    />
                  ) : (
                    <div className="text-xs text-zinc-500 italic mt-8">
                      Tip fișier necunoscut — folosește „Deschide" pentru a-l vizualiza.
                    </div>
                  )}
                </div>
              </div>

              {/* DREAPTA: verificare câmpuri */}
              <div className="overflow-y-auto pr-1 space-y-3" data-testid="talon-rescan-fields-pane">
                <div className="text-[11px] text-zinc-600 flex items-center justify-between bg-zinc-50 border rounded p-2 flex-wrap gap-2">
                  <span>
                    ✓ Talon procesat — verifică valorile din stânga și bifează câmpurile pe care vrei să le actualizezi.
                  </span>
                  <button
                    type="button"
                    onClick={() => { setExtracted(null); setSelected({}); setFile(null); }}
                    className="text-xs text-cyan-700 hover:text-cyan-900 underline"
                    data-testid="talon-rescan-restart"
                  >
                    Re-încarcă alt talon
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-2">
                  {TALON_FIELD_MAP.map(fieldRow)}
                </div>

                {/* Câmpuri extra detectate dar nemapate */}
                {(() => {
                  const knownKeys = new Set(TALON_FIELD_MAP.map((f) => f.key));
                  const extras = Object.entries(extracted).filter(
                    ([k, v]) => !knownKeys.has(k) && v != null && v !== ""
                  );
                  if (extras.length === 0) return null;
                  return (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-zinc-600 hover:text-zinc-900">
                        📋 Alte {extras.length} câmpuri detectate (informativ, nesalvate)
                      </summary>
                      <div className="mt-1.5 grid grid-cols-2 gap-1 bg-zinc-50 border rounded p-2 text-[11px]">
                        {extras.map(([k, v]) => (
                          <div key={k} className="flex gap-1">
                            <span className="text-zinc-500 font-mono">{k}:</span>
                            <span className="text-zinc-800 font-semibold truncate">{String(v)}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  );
                })()}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-2 border-t mt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Anulează</Button>
          {extracted && (
            <Button
              onClick={apply}
              disabled={busy || Object.values(selected).filter(Boolean).length === 0}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="talon-rescan-apply"
            >
              {busy ? "Se salvează..." : `Aplică ${Object.values(selected).filter(Boolean).length} câmp${Object.values(selected).filter(Boolean).length === 1 ? "" : "uri"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
